// stores.ts — persistence for the browser host shell. Everything persistent
// now lives in IndexedDB (the same storage class the WASM client's IDBFS used),
// hydrated to memory at boot so the synchronous C++-shaped APIs hold (reads
// serve from a map; writes flush through asynchronously, fire-and-forget —
// like the POSIX writes the C++ never checks either):
//   IdbPackStore     — the DLC pack cache (httppackmanager.ts SHAPE NOTE 1)
//   IdbSaveStore     — the save-game slots (the C++ web client's IDBFS saves
//                      dir; migrates the legacy LocalSaveStore localStorage
//                      document once at open())
//   IdbPrefStore     — HostGetPrefsFilename's prefs.json DOCUMENT BYTES (the
//                      C++ file format verbatim; migrates the legacy
//                      LocalPrefStore localStorage key once at open())
//   IdbCompleteStore — the completes dir: one zero-length record per completed
//                      mission, keyed by the C++ "kkkkkkkk-hhhh…" filename
//                      (migrates the legacy LocalCompleteStore key once)
//
// The retired localStorage-document stores (LocalPrefStore/LocalCompleteStore)
// are kept exported for tests and for their key constants; quota/security
// failures degrade to the in-memory behavior (the seam contracts allow
// failure). Migration FROM the WASM client's IDBFS database is web/migrate-wasm.ts.

import type { PrefStore } from '../io/prefs.ts';
import type { SaveStore, SaveGameRecord, SaveGameMeta } from '../io/savegame.ts';
import type { CompleteStore } from '../io/completemgr.ts';
import type { PackStore } from '../net/httppackmanager.ts';

function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

const bytesFromB64 = (s: string): Uint8Array => {
  const bin = atob(s);
  const b = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) b[i] = bin.charCodeAt(i);
  return b;
};

// ---- shared IndexedDB plumbing -----------------------------------------------

/** Open (creating if needed) the one-object-store database shape every TS
 *  store uses: `dbName` at version 1 holding object store `storeName` with
 *  out-of-line keys. Rejects where IndexedDB is unavailable (privacy modes) —
 *  callers degrade to memory-only. */
export function idbOpenStoreDb(dbName: string, storeName: string): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(dbName, 1);
    } catch (e) {
      reject(e);
      return;
    }
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(storeName)) {
        req.result.createObjectStore(storeName);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB.open failed'));
  });
}

/** Every key+value of one object store (the boot-hydration read). */
function idbGetAll(
  db: IDBDatabase,
  storeName: string,
): Promise<{ keys: IDBValidKey[]; values: unknown[] }> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const os = tx.objectStore(storeName);
    const reqK = os.getAllKeys();
    const reqV = os.getAll();
    tx.oncomplete = () => resolve({ keys: reqK.result, values: reqV.result });
    tx.onerror = () => reject(tx.error ?? new Error('idb hydrate failed'));
  });
}

/** Fire-and-forget IDB write-through (the seam contracts allow failure). */
function idbFlush(
  db: IDBDatabase | null,
  storeName: string,
  fn: (os: IDBObjectStore) => void,
): void {
  if (db === null) return;
  try {
    fn(db.transaction(storeName, 'readwrite').objectStore(storeName));
  } catch (e) {
    console.warn(`stores: ${storeName} write-through failed:`, e);
  }
}

const asBytes = (v: unknown): Uint8Array | null =>
  v instanceof Uint8Array ? v : v instanceof ArrayBuffer ? new Uint8Array(v) : null;

/** HostGetPrefsFilename's file as one localStorage key. RETIRED in favor of
 *  IdbPrefStore (kept for its one-time migration and for tests). */
export class LocalPrefStore implements PrefStore {
  private readonly key: string;
  constructor(key = 'wi.ts.prefs') {
    this.key = key;
  }
  read(): string | null {
    return lsGet(this.key);
  }
  write(json: string): boolean {
    return lsSet(this.key, json);
  }
}

/** The retired LocalSaveStore's document shape (one localStorage key holding
 *  every slot, bytes base64) — kept for IdbSaveStore.open()'s migration. */
interface SaveSlotJson {
  nGame: number;
  szName: string;
  msDate: number;
  b64: string;
}

/** The DLC cache directory (PackManager's "iiiiiiii-hhhh…32" files) over
 *  IndexedDB. `IdbPackStore.open()` hydrates every stored pack into memory
 *  BEFORE the synchronous PackStore API is used (InitFromInstalled at boot);
 *  IDB unavailability (privacy modes) degrades to memory-only. */
export class IdbPackStore implements PackStore {
  private readonly files = new Map<string, Uint8Array>();
  private db: IDBDatabase | null = null;
  private readonly storeName: string;

  private constructor(storeName: string) {
    this.storeName = storeName;
  }

  static async open(dbName = 'wi.ts.packs', storeName = 'files'): Promise<IdbPackStore> {
    const store = new IdbPackStore(storeName);
    try {
      store.db = await new Promise<IDBDatabase>((resolve, reject) => {
        const req = indexedDB.open(dbName, 1);
        req.onupgradeneeded = () => {
          if (!req.result.objectStoreNames.contains(storeName)) {
            req.result.createObjectStore(storeName);
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('indexedDB.open failed'));
      });
      // hydrate: every cached pack into the synchronous map
      const { keys, values } = await new Promise<{
        keys: IDBValidKey[];
        values: unknown[];
      }>((resolve, reject) => {
        const tx = store.db!.transaction(storeName, 'readonly');
        const os = tx.objectStore(storeName);
        const reqK = os.getAllKeys();
        const reqV = os.getAll();
        tx.oncomplete = () => resolve({ keys: reqK.result, values: reqV.result });
        tx.onerror = () => reject(tx.error ?? new Error('pack hydrate failed'));
      });
      for (let i = 0; i < keys.length; i++) {
        const v = values[i];
        if (typeof keys[i] === 'string' && v instanceof Uint8Array) {
          store.files.set(keys[i] as string, v);
        } else if (typeof keys[i] === 'string' && v instanceof ArrayBuffer) {
          store.files.set(keys[i] as string, new Uint8Array(v));
        }
      }
    } catch (e) {
      console.warn('IdbPackStore: IndexedDB unavailable — packs will not persist:', e);
      store.db = null;
    }
    return store;
  }

  /** Fire-and-forget IDB write-through (the seam contracts allow failure). */
  private flush(fn: (os: IDBObjectStore) => void): void {
    if (this.db === null) return;
    try {
      fn(this.db.transaction(this.storeName, 'readwrite').objectStore(this.storeName));
    } catch (e) {
      console.warn('IdbPackStore: write-through failed:', e);
    }
  }

  list(): string[] {
    return [...this.files.keys()];
  }

  read(filename: string): Uint8Array | null {
    return this.files.get(filename) ?? null;
  }

  write(filename: string, bytes: Uint8Array): void {
    const copy = bytes.slice();
    this.files.set(filename, copy);
    this.flush((os) => os.put(copy, filename));
  }

  remove(filename: string): boolean {
    const had = this.files.delete(filename);
    if (had) this.flush((os) => os.delete(filename));
    return had;
  }
}

interface IdbSaveSlot {
  nGame: number;
  szName: string;
  msDate: number;
  bytes: Uint8Array;
}

/** The POSIX saves directory over IndexedDB, one record per slot (keyed by
 *  nGame). Replaces LocalSaveStore: the C++ web client autosaves every 500ms
 *  (game.cpp:1833-1841) into IDBFS — effectively unbounded — while ONE
 *  localStorage JSON document re-serialized every save risks the ~5MB origin
 *  quota (lsSet fails silently) and main-thread stringify jank. Same shape as
 *  IdbPackStore: open() hydrates every slot into memory BEFORE the synchronous
 *  SaveStore API is used; writes flush through asynchronously (the C++ never
 *  checks its fwrites either); IDB unavailability degrades to memory-only.
 *  open() also migrates the legacy LocalSaveStore document once: slots import
 *  into IDB and the localStorage key is removed. */
export class IdbSaveStore implements SaveStore {
  private readonly slots = new Map<number, IdbSaveSlot>();
  private db: IDBDatabase | null = null;
  private readonly storeName: string;

  private constructor(storeName: string) {
    this.storeName = storeName;
  }

  static async open(
    dbName = 'wi.ts.saves',
    storeName = 'slots',
    legacyKey = 'wi.ts.saves',
  ): Promise<IdbSaveStore> {
    const store = new IdbSaveStore(storeName);
    try {
      store.db = await new Promise<IDBDatabase>((resolve, reject) => {
        const req = indexedDB.open(dbName, 1);
        req.onupgradeneeded = () => {
          if (!req.result.objectStoreNames.contains(storeName)) {
            req.result.createObjectStore(storeName);
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('indexedDB.open failed'));
      });
      const values = await new Promise<unknown[]>((resolve, reject) => {
        const tx = store.db!.transaction(storeName, 'readonly');
        const req = tx.objectStore(storeName).getAll();
        tx.oncomplete = () => resolve(req.result);
        tx.onerror = () => reject(tx.error ?? new Error('save hydrate failed'));
      });
      for (const v of values) {
        const s = v as IdbSaveSlot;
        if (s !== null && typeof s === 'object' && typeof s.nGame === 'number') {
          store.slots.set(s.nGame, {
            nGame: s.nGame,
            szName: s.szName,
            msDate: s.msDate,
            bytes:
              s.bytes instanceof Uint8Array ? s.bytes : new Uint8Array(s.bytes as ArrayBufferLike),
          });
        }
      }
    } catch (e) {
      console.warn('IdbSaveStore: IndexedDB unavailable — saves will not persist:', e);
      store.db = null;
    }

    // one-time migration of the legacy single-document LocalSaveStore key:
    // import slots IDB doesn't already have, then remove the document so the
    // user's existing saves survive the storage move (and migrate only once)
    const legacy = lsGet(legacyKey);
    if (legacy !== null) {
      try {
        const v = JSON.parse(legacy) as SaveSlotJson[];
        if (Array.isArray(v)) {
          for (const s of v) {
            if (typeof s?.nGame !== 'number' || store.slots.has(s.nGame)) continue;
            store.put({
              nGame: s.nGame,
              szName: s.szName,
              msDate: s.msDate,
              bytes: bytesFromB64(s.b64),
            });
          }
        }
      } catch (e) {
        console.warn('IdbSaveStore: legacy save migration failed:', e);
      }
      try {
        localStorage.removeItem(legacyKey);
      } catch {
        /* quota/privacy */
      }
    }
    return store;
  }

  /** Fire-and-forget IDB write-through (the seam contracts allow failure). */
  private flush(fn: (os: IDBObjectStore) => void): void {
    if (this.db === null) return;
    try {
      fn(this.db.transaction(this.storeName, 'readwrite').objectStore(this.storeName));
    } catch (e) {
      console.warn('IdbSaveStore: write-through failed:', e);
    }
  }

  get(nGame: number): SaveGameRecord | null {
    const s = this.slots.get(nGame);
    if (s === undefined) return null;
    return { nGame: s.nGame, szName: s.szName, msDate: s.msDate, bytes: s.bytes };
  }

  put(rec: SaveGameRecord): void {
    const slot: IdbSaveSlot = {
      nGame: rec.nGame,
      szName: rec.szName,
      msDate: rec.msDate,
      bytes: rec.bytes.slice(),
    };
    this.slots.set(slot.nGame, slot);
    this.flush((os) => os.put(slot, slot.nGame));
  }

  delete(nGame: number): boolean {
    const had = this.slots.delete(nGame);
    // Always write the delete through to IDB — NOT gated on `had`. A fire-and-forget put/delete
    // can leave IDB holding a record the in-memory map no longer has (or that a prior session
    // wrote); a had-gated flush would then never remove it, so the ghost resurrects on the next
    // open() hydrate (this was the "a refresh drops you back into an old game" bug). Deleting a
    // key IDB doesn't have is a harmless no-op.
    this.flush((os) => os.delete(nGame));
    return had;
  }

  list(): SaveGameMeta[] {
    return [...this.slots.values()].map(({ nGame, szName, msDate }) => ({ nGame, szName, msDate }));
  }
}

/** The completes directory (zero-length marker files) as one localStorage key.
 *  RETIRED in favor of IdbCompleteStore (kept for its one-time migration and
 *  for tests). */
export class LocalCompleteStore implements CompleteStore {
  private readonly key: string;
  constructor(key = 'wi.ts.completes') {
    this.key = key;
  }

  private load(): string[] {
    const json = lsGet(this.key);
    if (json === null) return [];
    try {
      const v = JSON.parse(json) as string[];
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  }

  list(): string[] {
    return this.load();
  }

  create(filename: string): boolean {
    const files = this.load();
    if (!files.includes(filename)) {
      files.push(filename);
      return lsSet(this.key, JSON.stringify(files));
    }
    return true;
  }
}

/** HostGetPrefsFilename's prefs.json over IndexedDB: ONE record holding the
 *  raw DOCUMENT BYTES under its C++ filename — the same bytes the C++
 *  HostWriteFile persisted (preferences.cpp Save = the map's JSON text; the
 *  parse happens at load, exactly like InitFromFile). Same shape as the other
 *  Idb stores: open() hydrates before the synchronous PrefStore API is used;
 *  writes flush through asynchronously; IDB unavailability degrades to
 *  memory-only. open() migrates the legacy LocalPrefStore localStorage key
 *  once (imported only when IDB holds no document — the IDB document is newer
 *  by construction, the localStorage one stopped being written when this
 *  store shipped). */
export class IdbPrefStore implements PrefStore {
  private json: string | null = null;
  private db: IDBDatabase | null = null;
  private readonly storeName: string;
  private readonly recordKey: string;

  private constructor(storeName: string, recordKey: string) {
    this.storeName = storeName;
    this.recordKey = recordKey;
  }

  static async open(
    dbName = 'wi.ts.prefs',
    storeName = 'files',
    recordKey = 'prefs.json',
    legacyKey = 'wi.ts.prefs',
  ): Promise<IdbPrefStore> {
    const store = new IdbPrefStore(storeName, recordKey);
    try {
      store.db = await idbOpenStoreDb(dbName, storeName);
      const { keys, values } = await idbGetAll(store.db, storeName);
      const i = keys.indexOf(recordKey);
      const bytes = i >= 0 ? asBytes(values[i]) : null;
      if (bytes !== null) store.json = new TextDecoder().decode(bytes);
    } catch (e) {
      console.warn('IdbPrefStore: IndexedDB unavailable — prefs will not persist:', e);
      store.db = null;
    }

    // one-time migration of the legacy localStorage document
    const legacy = lsGet(legacyKey);
    if (legacy !== null) {
      if (store.json === null) store.write(legacy);
      try {
        localStorage.removeItem(legacyKey);
      } catch {
        /* quota/privacy */
      }
    }
    return store;
  }

  read(): string | null {
    return this.json;
  }

  write(json: string): boolean {
    this.json = json;
    const bytes = new TextEncoder().encode(json);
    idbFlush(this.db, this.storeName, (os) => os.put(bytes, this.recordKey));
    return true;
  }
}

/** The completes directory over IndexedDB: one zero-length record per
 *  completed mission, keyed by the C++ "kkkkkkkk-hhhh…32" filename — the
 *  filename IS the datum (completemanager.cpp's zero-length marker files).
 *  open() hydrates + migrates the legacy LocalCompleteStore key once (a
 *  union: completes are monotone marks, so merging both sources is exact). */
export class IdbCompleteStore implements CompleteStore {
  private readonly files = new Set<string>();
  private db: IDBDatabase | null = null;
  private readonly storeName: string;

  private constructor(storeName: string) {
    this.storeName = storeName;
  }

  static async open(
    dbName = 'wi.ts.completes',
    storeName = 'files',
    legacyKey = 'wi.ts.completes',
  ): Promise<IdbCompleteStore> {
    const store = new IdbCompleteStore(storeName);
    try {
      store.db = await idbOpenStoreDb(dbName, storeName);
      const { keys } = await idbGetAll(store.db, storeName);
      for (const k of keys) {
        if (typeof k === 'string') store.files.add(k);
      }
    } catch (e) {
      console.warn('IdbCompleteStore: IndexedDB unavailable — completes will not persist:', e);
      store.db = null;
    }

    // one-time migration of the legacy localStorage document (union)
    const legacy = lsGet(legacyKey);
    if (legacy !== null) {
      try {
        const v = JSON.parse(legacy) as string[];
        if (Array.isArray(v)) {
          for (const f of v) {
            if (typeof f === 'string') store.create(f);
          }
        }
      } catch (e) {
        console.warn('IdbCompleteStore: legacy completes migration failed:', e);
      }
      try {
        localStorage.removeItem(legacyKey);
      } catch {
        /* quota/privacy */
      }
    }
    return store;
  }

  list(): string[] {
    return [...this.files];
  }

  create(filename: string): boolean {
    if (!this.files.has(filename)) {
      this.files.add(filename);
      idbFlush(this.db, this.storeName, (os) => os.put(new Uint8Array(0), filename));
    }
    return true;
  }
}
